/**
 * FileManagement — Utility Functions
 *
 * Pure helper functions used across the file management feature.
 * No side effects, no React imports, no API calls — just data transformations.
 */

import { getFileTypeIcon } from "../../../../../../../../utils/functions/file-icon-extractor";
import { getDateTimeByFormat } from "../../../../../../../../redux/slices/dateTimeFormatSlice";
import {
  ALLOWED_EXTENSIONS,
  KB_FILES_LIMITS,
} from "../constants/fileManagement.constants";
import type {
  KBFileEntity,
  KBFileType,
  KBFileListParams,
  ToastOptions,
  ToastVariant,
} from "../types/fileManagement.types";
import { customToast } from "../../../../../../../../utils/custom-toast/custom-toast";
import {
  IMAGE_PREVIEW_TYPES,
  DOCUMENT_PREVIEW_TYPES,
  WINDOW_OPEN_TARGET,
  WINDOW_OPEN_FEATURES,
  TOAST_URL_COPIED,
  TOAST_URL_COPY_FAILED,
  BREADCRUMB_ROOT_LABEL,
  BREADCRUMB_ELLIPSIS,
  BREADCRUMB_MAX_VISIBLE_SEGMENTS,
  BREADCRUMB_TAIL_COUNT,
  ROW_ACTION_MENU_WIDTH,
  ROW_ACTION_MENU_ITEM_HEIGHT,
  ROW_ACTION_MENU_PADDING,
  SORT_MENU_WIDTH,
  SORT_MENU_ITEM_HEIGHT,
  SORT_MENU_PADDING,
  HTML_PREVIEW_MIN_HEIGHT_PX,
  HTML_PREVIEW_MAX_HEIGHT_PX,
  HTML_PREVIEW_BOTTOM_PADDING_PX,
} from "../constants/fileManagement.constants";
import { getClientDomain } from "../../../../../../../../utils/functions/client-code";
import { v4 as uuidv4 } from "uuid";

// ---------------------------------------------------------------------------
// Type Checks
// ---------------------------------------------------------------------------

/** Returns true if the entity is a folder (not a file). */
export const isFolder = (entity: KBFileEntity): boolean => {
  return entity.type === "folder";
};

/** Returns true if the file type is a previewable image format. */
export const isImageFile = (type: string): boolean =>
  IMAGE_PREVIEW_TYPES.includes(
    type.toLowerCase() as (typeof IMAGE_PREVIEW_TYPES)[number]
  );

/** Returns true if the file type is a document format. */
export const isDocumentFile = (type: string): boolean =>
  DOCUMENT_PREVIEW_TYPES.includes(
    type.toLowerCase() as (typeof DOCUMENT_PREVIEW_TYPES)[number]
  );

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * Converts bytes into a human-readable file size string.
 *
 * Examples:
 *   formatFileSize(0)         → "0 B"
 *   formatFileSize(1024)      → "1.00 KB"
 *   formatFileSize(1234567)   → "1.18 MB"
 *   formatFileSize(1500000000) → "1.40 GB"
 */
export const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  let unitIndex = 0;
  let size = bytes;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
};

/**
 * Formats an ISO date string using the project's global date/time format settings.
 * Uses the same getDateTimeByFormat() function as ManageDocumentsTable.
 *
 * Example: "2023-12-01T10:00:00Z" → "01 Dec 2023, 10:00"
 */
export const formatDate = (isoString: string): string => {
  if (!isoString) return "-";

  const dateObj = new Date(isoString);
  if (isNaN(dateObj.getTime())) return "-";

  const { date, time } = getDateTimeByFormat(dateObj);
  if (!date) return "-";

  return time ? `${date}, ${time}` : date;
};

/**
 * Returns the icon source (SVG path) for a given file type.
 * Leverages the existing getFileTypeIcon() utility which maps extensions to SVG icons.
 *
 * For folders: passes "file.folder" to extract "folder" extension.
 * For files: passes "file.<type>" to extract the correct extension.
 */
export const getIconForFileType = (type: string): string => {
  if (type === "folder") {
    return getFileTypeIcon("file.folder");
  }
  return getFileTypeIcon(`file.${type}`);
};

// ---------------------------------------------------------------------------
// File Details Drawer Helpers
// ---------------------------------------------------------------------------

/** Returns true if the file type can render an image preview. */
export const isImageType = (type: KBFileType): boolean => {
  return IMAGE_PREVIEW_TYPES.includes(type);
};

/** Returns true if the file type is a document (shows centered icon preview). */
export const isDocumentType = (type: KBFileType): boolean => {
  return DOCUMENT_PREVIEW_TYPES.includes(type);
};

/**
 * Strips the protocol from a URL and returns the domain + path.
 * Falls back to the original string if parsing fails.
 */
// export const getShortenedUrl = (url: string): string => {
//   if (!url) return "";
//   try {
//     const parsed = new URL(url);
//     return `${parsed.host}${parsed.pathname}`;
//   } catch {
//     return url;
//   }
// };

/** Copies text to the clipboard. Returns true on success. */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/** Copies text to clipboard and shows a success/error toast. */
export const copyToClipboardWithToast = async (text: string): Promise<void> => {
  const success = await copyToClipboard(text);
  customToast(
    success ? TOAST_URL_COPIED : TOAST_URL_COPY_FAILED,
    success ? "success" : "error"
  );
};

/**
 * Wrapper around customToast with sensible defaults for the file management feature
 * (3s duration, bottom-right by default). Pass `position: "bottom-left"` for drawer toasts.
 */
const showToast = (
  type: ToastVariant,
  message: string,
  options?: ToastOptions
) =>
  customToast(
    message,
    type,
    options?.duration ?? 3000,
    options?.subMessage,
    options?.position ?? "bottom-right"
  );

export const showSuccessToast = (message: string, options?: ToastOptions) =>
  showToast("success", message, options);

export const showErrorToast = (message: string, options?: ToastOptions) =>
  showToast("error", message, options);

export const showWarningToast = (message: string, options?: ToastOptions) =>
  showToast("warning", message, options);

export const showInfoToast = (message: string, options?: ToastOptions) =>
  showToast("info", message, options);

/** Returns the best available URL for a file (shortened first, then s3). */
export const getFileUrl = (file: KBFileEntity): string => {
  return file.short_url || "";
};

/** Opens a URL in a new browser tab/window. */
export const openUrlInNewWindow = (url: string): void => {
  if (url) window.open(url, WINDOW_OPEN_TARGET, WINDOW_OPEN_FEATURES);
};

/**
 * Measures the content height of an HTML preview iframe and returns a clamped
 * value (within the configured min/max bounds) suitable for setting as the
 * wrapper's height.
 *
 * Returns `null` when the height cannot be measured — most commonly because
 * the iframe is cross-origin and the browser blocks contentDocument access.
 * The caller can fall back to its default in that case.
 */
export const getClampedHtmlPreviewHeight = (
  iframeElement: HTMLIFrameElement
): number | null => {
  try {
    const measuredContentHeight =
      iframeElement.contentDocument?.body?.scrollHeight;
    if (!measuredContentHeight) return null;

    return Math.min(
      Math.max(
        measuredContentHeight + HTML_PREVIEW_BOTTOM_PADDING_PX,
        HTML_PREVIEW_MIN_HEIGHT_PX
      ),
      HTML_PREVIEW_MAX_HEIGHT_PX
    );
  } catch {
    return null;
  }
};

/**
 * Splits file_expiration_time into separate date/time display strings
 * plus raw picker-ready values (Date for the date picker, "HH:mm" for the time picker).
 */
export const parseExpirationDateTime = (
  isoString?: string
): {
  dateDisplay: string | null;
  timeDisplay: string | null;
  pickerDate: Date | null;
  pickerTime: string | null;
} => {
  const empty = {
    dateDisplay: null,
    timeDisplay: null,
    pickerDate: null,
    pickerTime: null,
  };
  if (!isoString) return empty;
  const dateObj = new Date(isoString);
  if (isNaN(dateObj.getTime())) return empty;

  const formatted = formatDate(isoString);
  const [datePart, timePart] = formatted.split(",");

  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");

  return {
    dateDisplay: datePart?.trim() || null,
    timeDisplay: timePart?.trim() || null,
    pickerDate: dateObj,
    pickerTime: `${hours}:${minutes}`,
  };
};

/**
 * Merges a local Date and an optional "HH:mm" time string into a UTC ISO string
 * suitable for sending to the backend as file_expiration_time.
 * When no time is provided, defaults to 23:59:59 local time.
 */
export const localDateToUtcIsoString = (
  localDate: Date,
  timeString?: string
): string => {
  const merged = new Date(localDate);
  if (timeString) {
    const [hours, minutes] = timeString.split(":").map(Number);
    merged.setHours(hours, minutes, 0, 0);
  } else {
    merged.setHours(23, 59, 59, 0);
  }
  return merged.toISOString();
};

/**
 * Returns tomorrow at 23:59:59 local time converted to a UTC ISO string.
 * Used as the default file_expiration_time when a file has none yet.
 */
export const getDefaultExpirationIso = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(23, 59, 59, 0);
  return tomorrow.toISOString();
};

/** Creates a temporary anchor to trigger a file download from a Blob. */
export const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Fetch interceptor that injects the standard client headers
 * (X-Client-Code when available + X-Hash-Key request id) and
 * sane defaults (credentials + no-store) used across the app.
 */
export const fetchWithClientHeaders = (
  url: string,
  init: RequestInit = {}
): Promise<Response> => {
  const clientCode = getClientDomain();
  const requestId = uuidv4();
  return fetch(url, {
    ...init,
    credentials: "include",
    cache: "no-store",
    headers: {
      ...(init.headers ?? {}),
      ...(clientCode ? { "X-Client-Code": `${clientCode}` } : {}),
      "X-Hash-Key": requestId,
    },
  });
};

/**
 * Fetches the file as a blob and triggers a save-as download via an Object URL.
 * Works across origins where the <a download> attribute would otherwise be ignored.
 */
export const triggerFileDownloadByUrl = async (
  url: string,
  fileName: string
): Promise<void> => {
  const response = await fetchWithClientHeaders(url, { method: "GET" });
  if (!response.ok) throw new Error("Download failed");

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(objectUrl);
};

/**
 * Returns the earliest time the user is allowed to pick when expiration is today.
 * Rounds "now" UP to the next 15-minute slot ("HH:MM"), so past slots are never selectable.
 *
 * Examples (assuming "now" is the value on the left):
 *   01:00 -> 01:15   (already on a quarter, jump to the next one)
 *   01:07 -> 01:15   (round up 8 minutes)
 *   01:23 -> 01:30   (round up 7 minutes)
 *   23:50 -> 00:00   (rolls into the next hour automatically)
 */
export const getEarliestQuarterHourFromNow = (): string => {
  const now = new Date();
  const next = new Date(now);
  next.setMinutes(now.getMinutes() + (15 - (now.getMinutes() % 15)), 0, 0);
  return next.toTimeString().slice(0, 5);
};

/**
 * Returns the current local time as "HH:mm".
 * Used to allow manual entry of any minute strictly after now,
 * even when it falls between the dropdown's 15-minute slots.
 */
export const getCurrentHHMM = (): string => {
  return new Date().toTimeString().slice(0, 5);
};

/** Builds the breadcrumb segment strings for the file details drawer header. */
export const buildDrawerBreadcrumbs = (
  navigationStack: { name: string }[],
  fileName: string
): string[] => {
  const crumbs = [...navigationStack.map((item) => item.name), fileName];
  const segments: string[] = [BREADCRUMB_ROOT_LABEL];

  if (crumbs.length <= BREADCRUMB_MAX_VISIBLE_SEGMENTS) {
    segments.push(...crumbs);
  } else {
    segments.push(BREADCRUMB_ELLIPSIS);
    segments.push(...crumbs.slice(-BREADCRUMB_TAIL_COUNT));
  }

  return segments;
};

// ---------------------------------------------------------------------------
// Query Param Builder
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Row Actions Menu Helpers
// ---------------------------------------------------------------------------

/**
 * Calculates the fixed position for the row actions popup menu
 * based on the trigger button's bounding rect.
 * Opens below the button when space allows, otherwise opens above.
 */
export const calculateRowActionMenuPosition = (
  triggerRect: DOMRect,
  itemCount: number
): { x: number; y: number } => {
  const menuHeight =
    itemCount * ROW_ACTION_MENU_ITEM_HEIGHT + ROW_ACTION_MENU_PADDING;
  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const y =
    spaceBelow < menuHeight
      ? triggerRect.top - menuHeight
      : triggerRect.bottom + 4;
  const x = triggerRect.right - ROW_ACTION_MENU_WIDTH;
  return { x: Math.max(4, x), y };
};

/**
 * Calculates the fixed position for the column sort popup menu
 * based on the sort trigger's bounding rect.
 * Opens below the trigger when space allows, otherwise opens above.
 */
export const calculateSortMenuPosition = (
  triggerRect: DOMRect,
  itemCount: number
): { x: number; y: number } => {
  const menuHeight = itemCount * SORT_MENU_ITEM_HEIGHT + SORT_MENU_PADDING;
  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const y =
    spaceBelow < menuHeight
      ? triggerRect.top - menuHeight
      : triggerRect.bottom + 4;
  const maxX = window.innerWidth - SORT_MENU_WIDTH - 4;
  const x = Math.min(Math.max(4, triggerRect.left), maxX);
  return { x, y };
};

// ---------------------------------------------------------------------------
// Upload Validation
// ---------------------------------------------------------------------------

/**
 * Validates a single File object before adding it to the upload queue.
 * Returns an error message string if invalid, or null if the file is OK.
 */
export const validateFile = (file: File): string | null => {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (
    !ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])
  ) {
    return `"${file.name}" — file type not supported. Please upload a supported format.`;
  }
  if (file.size > KB_FILES_LIMITS.MAX_FILE_SIZE_BYTES) {
    return `"${file.name}" exceeds the maximum file size limit of 25 MB. Please upload a smaller file.`;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Query Param Builder
// ---------------------------------------------------------------------------

/**
 * Builds a clean query params object from KBFileListParams.
 * Strips out undefined, null, and empty string values so the API
 * doesn't receive unnecessary query parameters.
 */
export const buildListQueryParams = (
  params: KBFileListParams
): Record<string, string> => {
  const result: Record<string, string> = {};

  if (params.parent_folder_id) {
    result.parent_folder_id = params.parent_folder_id;
  }
  if (params.page) {
    result.page = String(params.page);
  }
  if (params.limit) {
    result.limit = String(params.limit);
  }
  if (params.sort_key) {
    result.sort_key = params.sort_key;
  }
  if (params.sort_order) {
    result.sort_order = params.sort_order;
  }
  if (params.search) {
    result.search = params.search;
  }
  if (params.types) {
    result.types = params.types;
  }
  if (params.uploaded_by) {
    result.uploaded_by = params.uploaded_by;
  }
  if (params.last_updated_by) {
    result.last_updated_by = params.last_updated_by;
  }
  if (params.uploaded_date_from) {
    result.uploaded_date_from = params.uploaded_date_from;
  }
  if (params.uploaded_date_to) {
    result.uploaded_date_to = params.uploaded_date_to;
  }

  return result;
};
